/**
 * #1202 — the sport of each Polymarket US market on Org Detail, for the page's
 * sport chips.
 *
 * Same rule as the Tracker's US chips (`usMarketClass`: the catalogue's own
 * `sport`, by exact slug; a market the catalogue does not hold is pending),
 * read through the ACCOUNT-FREE catalogue read (`/markets?slugs=`, at most 20
 * exact slugs per read). The Tracker's classifier reads through the account's
 * own route, which needs the viewer's grant on that account; a manager looking
 * at the org's accounts may hold none. Nothing is ever parsed from a slug or
 * a title.
 *
 * The host asks only while a chip is on. `request(slugs)` names the markets
 * wanted now: it replaces any queued work, so a chip turned off (an empty
 * request) drops the queue while a read already sent finishes and is kept.
 * Reads run one at a time (the market client lets only its latest read win).
 * A market found is kept for the mount. A miss is asked again by a later
 * request once `US_MARKET_MISS_RETRY_MS` has passed, or at once after
 * `retryMisses()` (the page's Refresh).
 */
import { US_MARKET_MISS_RETRY_MS, usMarketClass, type UsMarketClass } from '@/lib/polymarketUs/marketFamily';
import type { UsMarketClient } from '@/lib/polymarketUs/marketClient';

/** The catalogue read's own limits: exact slugs, 20 per read. */
const SLUG = /^[A-Za-z0-9_-]{1,256}$/;
export const US_ORG_SPORT_CHUNK = 20;

type Entry = { cls: UsMarketClass } | { missAtMs: number };

export class UsOrgSportLookup {
  private readonly entries = new Map<string, Entry>();
  private queue: string[] = [];
  private readonly inFlight = new Set<string>();
  private running = false;
  private closed = false;
  private version = 0;
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly client: Pick<UsMarketClient, 'listPublicMarkets'>,
    private readonly clock: () => number = Date.now,
  ) {}

  /** null = pending: not looked up yet, the catalogue does not hold it, or the read failed. */
  get(slug: string): UsMarketClass | null {
    const entry = this.entries.get(slug);
    return entry && 'cls' in entry ? entry.cls : null;
  }

  getVersion = (): number => this.version;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };

  /** The markets wanted now. Replaces queued work; skips markets known, in
   * flight, missed within the retry window, or not a valid slug (never sent,
   * so it stays pending). */
  request(slugs: Iterable<string>): void {
    if (this.closed) return;
    const now = this.clock();
    const next = new Set<string>();
    for (const slug of slugs) {
      if (!SLUG.test(slug) || this.inFlight.has(slug)) continue;
      const entry = this.entries.get(slug);
      if (entry && ('cls' in entry || now - entry.missAtMs < US_MARKET_MISS_RETRY_MS)) continue;
      next.add(slug);
    }
    this.queue = [...next];
    void this.pump();
  }

  /** Make every miss askable again; the next request reads them. */
  retryMisses(): void {
    for (const [slug, entry] of this.entries) if ('missAtMs' in entry) this.entries.delete(slug);
  }

  /** Mounted (again): StrictMode's replayed unmount must not leave it shut. */
  open(): void {
    this.closed = false;
  }

  /** Unmounted: drop queued work; an answer that lands while closed is ignored. */
  close(): void {
    this.closed = true;
    this.queue = [];
  }

  private async pump(): Promise<void> {
    if (this.running) return;
    this.running = true;
    try {
      while (!this.closed && this.queue.length > 0) {
        const chunk = this.queue.splice(0, US_ORG_SPORT_CHUNK);
        for (const slug of chunk) this.inFlight.add(slug);
        const found = new Map<string, UsMarketClass>();
        try {
          const page = await this.client.listPublicMarkets({ slugs: chunk });
          for (const market of page.markets) found.set(market.marketSlug, usMarketClass(market));
        } catch {
          // Catalogue unavailable, refused, or a malformed answer: every market in it is a miss.
        }
        for (const slug of chunk) this.inFlight.delete(slug);
        if (this.closed) return;
        const missAtMs = this.clock();
        for (const slug of chunk) {
          const cls = found.get(slug);
          this.entries.set(slug, cls ? { cls } : { missAtMs });
        }
        if (found.size > 0) {
          this.version++;
          for (const listener of this.listeners) listener();
        }
      }
    } finally {
      this.running = false;
    }
  }
}
